// components/session/ActivityTracker.js - SURVEILLANCE ACTIVITÉ UTILISATEUR
import { useEffect, useRef, useCallback } from 'react';

/**
 * 🔥 ACTIVITY TRACKER - SURVEILLANCE INTELLIGENTE DE L'ACTIVITÉ
 * - Détecte activité forte (clics, clavier) et faible (souris, scroll)
 * - Throttling intelligent pour performance
 * - Communication avec SessionManager
 */
const ActivityTracker = ({ onActivity, isActive = true }) => {
  // Refs pour throttling
  const throttleTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const mouseThrottleRef = useRef(null);
  const scrollThrottleRef = useRef(null);
  const keyboardThrottleRef = useRef(null);

  // Configuration
  const config = {
    MOUSE_THROTTLE: 1000,    // 1s pour mousemove
    SCROLL_THROTTLE: 500,    // 500ms pour scroll
    KEYBOARD_THROTTLE: 100,  // 100ms pour clavier
    ACTIVITY_THROTTLE: 1000, // 1s entre reports d'activité
  };

  /**
   * 🔥 THROTTLE GÉNÉRALISÉ
   */
  const throttle = useCallback((func, delay, throttleRef) => {
    return (...args) => {
      if (throttleRef.current) return;
      
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
        func(...args);
      }, delay);
    };
  }, []);

  /**
   * 🔥 REPORTER ACTIVITÉ (THROTTLÉ)
   */
  const reportActivity = useCallback((activityType, intensity = 'normal') => {
    if (!isActive || !onActivity) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Throttling global des reports d'activité
    if (timeSinceLastActivity < config.ACTIVITY_THROTTLE) return;

    lastActivityRef.current = now;
    onActivity(activityType, intensity);
  }, [isActive, onActivity, config.ACTIVITY_THROTTLE]);

  /**
   * 🔥 GESTIONNAIRES D'ÉVÉNEMENTS
   */

  // ACTIVITÉ FORTE - Reset complet du timer
  const handleStrongActivity = useCallback((event) => {
    reportActivity(event.type, 'strong');
  }, [reportActivity]);

  // ACTIVITÉ MOYENNE - Clavier avec throttling
  const handleKeyboardActivity = useCallback(
    throttle((event) => {
      // Ignorer les touches de modification seules
      if (['Shift', 'Control', 'Alt', 'Meta', 'Tab'].includes(event.key)) return;
      reportActivity('keyboard', 'normal');
    }, config.KEYBOARD_THROTTLE, keyboardThrottleRef),
    [reportActivity, config.KEYBOARD_THROTTLE]
  );

  // ACTIVITÉ FAIBLE - Souris avec throttling lourd
  const handleMouseActivity = useCallback(
    throttle((event) => {
      // Seulement si proche de l'expiration (sera géré par SessionManager)
      reportActivity('mouse', 'weak');
    }, config.MOUSE_THROTTLE, mouseThrottleRef),
    [reportActivity, config.MOUSE_THROTTLE]
  );

  // ACTIVITÉ FAIBLE - Scroll avec throttling
  const handleScrollActivity = useCallback(
    throttle((event) => {
      reportActivity('scroll', 'weak');
    }, config.SCROLL_THROTTLE, scrollThrottleRef),
    [reportActivity, config.SCROLL_THROTTLE]
  );

  // FOCUS/BLUR - Changement de fenêtre
  const handleFocusActivity = useCallback((event) => {
    reportActivity(event.type === 'focus' ? 'window_focus' : 'window_blur', 'normal');
  }, [reportActivity]);

  // NAVIGATION - Changement de route (React Router)
  const handleNavigationActivity = useCallback(() => {
    reportActivity('navigation', 'strong');
  }, [reportActivity]);

  /**
   * 🔥 INSTALLATION DES LISTENERS
   */
  useEffect(() => {
    if (!isActive) return;

    console.log('👁️ [ActivityTracker] Surveillance activité activée');

    // ACTIVITÉS FORTES (reset complet)
    const strongEvents = ['click', 'mousedown', 'submit'];
    strongEvents.forEach(event => {
      document.addEventListener(event, handleStrongActivity, { passive: true });
    });

    // ACTIVITÉ CLAVIER (throttlée)
    document.addEventListener('keydown', handleKeyboardActivity, { passive: true });
    document.addEventListener('input', handleStrongActivity, { passive: true });
    document.addEventListener('change', handleStrongActivity, { passive: true });

    // ACTIVITÉS FAIBLES (très throttlées)
    document.addEventListener('mousemove', handleMouseActivity, { passive: true });
    document.addEventListener('scroll', handleScrollActivity, { passive: true });

    // FOCUS/BLUR FENÊTRE
    window.addEventListener('focus', handleFocusActivity, { passive: true });
    window.addEventListener('blur', handleFocusActivity, { passive: true });

    // NAVIGATION (hashchange pour SPA)
    window.addEventListener('hashchange', handleNavigationActivity, { passive: true });
    window.addEventListener('popstate', handleNavigationActivity, { passive: true });

    // Nettoyage
    return () => {
      console.log('🔄 [ActivityTracker] Surveillance activité arrêtée');

      // Nettoyer les événements
      strongEvents.forEach(event => {
        document.removeEventListener(event, handleStrongActivity);
      });
      
      document.removeEventListener('keydown', handleKeyboardActivity);
      document.removeEventListener('input', handleStrongActivity);
      document.removeEventListener('change', handleStrongActivity);
      document.removeEventListener('mousemove', handleMouseActivity);
      document.removeEventListener('scroll', handleScrollActivity);
      
      window.removeEventListener('focus', handleFocusActivity);
      window.removeEventListener('blur', handleFocusActivity);
      window.removeEventListener('hashchange', handleNavigationActivity);
      window.removeEventListener('popstate', handleNavigationActivity);

      // Nettoyer les timeouts
      if (mouseThrottleRef.current) clearTimeout(mouseThrottleRef.current);
      if (scrollThrottleRef.current) clearTimeout(scrollThrottleRef.current);
      if (keyboardThrottleRef.current) clearTimeout(keyboardThrottleRef.current);
      if (throttleTimeoutRef.current) clearTimeout(throttleTimeoutRef.current);
    };
  }, [
    isActive,
    handleStrongActivity,
    handleKeyboardActivity,
    handleMouseActivity,
    handleScrollActivity,
    handleFocusActivity,
    handleNavigationActivity
  ]);

  /**
   * 🔥 SURVEILLANCE REACT ROUTER (SI DISPONIBLE)
   */
  useEffect(() => {
    // Détecter les changements de route React Router
    const currentPath = window.location.pathname;
    
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPath && isActive) {
        handleNavigationActivity();
      }
    });

    observer.observe(document.querySelector('title') || document.head, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [isActive, handleNavigationActivity]);

  /**
   * 🔥 DÉTECTION INACTIVITÉ PROLONGÉE
   */
  useEffect(() => {
    if (!isActive) return;

    const checkInactivity = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      // Si inactif depuis plus de 5 minutes, le signaler
      if (timeSinceLastActivity > 5 * 60 * 1000) {
        reportActivity('inactivity_detected', 'none');
        lastActivityRef.current = Date.now(); // Reset pour éviter spam
      }
    }, 60 * 1000); // Vérifier toutes les minutes

    return () => clearInterval(checkInactivity);
  }, [isActive, reportActivity]);

  // Component invisible - ne rend rien
  return null;
};

export default ActivityTracker;